import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import ReilmLogo from '../components/ReilmLogo'
import { reilmAPI } from '../services/apiClient'
import { useAuth } from '../features/auth/AuthContext'

const PLAN_LABEL = { pro: 'Pro', starter: 'Starter', ultimate: 'Ultimate' }

const CheckBadge = () => (
  <div className="w-[120px] h-[120px] rounded-full bg-[#e4f3da] flex items-center justify-center">
    <svg width="56" height="56" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" fill="#4cbc18" />
      <path d="M7 12.5l3.5 3.5L17 9" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </div>
)

const PaymentSuccess = () => {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { refreshUser } = useAuth()
  const [subscription, setSubscription] = useState(null)

  const planFromUrl = params.get('plan')
  const billingFromUrl = params.get('billing')

  useEffect(() => {
    reilmAPI
      .getSubscriptionStatus()
      .then((res) => setSubscription(res.data?.data))
      .catch(() => {})
    // The webhook has just changed this user's plan server-side. Pull the fresh record into
    // the session too, otherwise the pricing cards still show "Purchase" after paying.
    refreshUser()
  }, [refreshUser])

  const planKey = planFromUrl || subscription?.tier
  const planLabel = planKey ? PLAN_LABEL[planKey] || planKey : null
  // The URL carries the frontend's own vocabulary ('annual'), while the server stores
  // 'yearly'. Accept both, or falling back to server data labels a yearly plan "monthly" —
  // the same wrong-billing bug Микола reported, reached without the query params.
  const rawBilling = billingFromUrl || subscription?.billingCycle
  const billingLabel = rawBilling === 'annual' || rawBilling === 'yearly' ? 'yearly' : 'monthly'
  // If they arrived here via Subscribe (plan param in URL), they just paid — not on trial.
  // Trial messaging is only for flows that land here without an explicit paid checkout.
  const isTrial = !planFromUrl && subscription?.status === 'trialing'

  return (
    <div className="min-h-screen bg-brand-pageFill flex flex-col">
      <header className="h-[96px] px-6 md:px-[120px] flex items-center">
        <Link to="/" className="flex items-center group" aria-label="Arvance home">
          <ReilmLogo className="w-[131px] h-[28px] md:w-[168px] md:h-[36px]" />
        </Link>
      </header>

      {/* items-start, matching the auth pages — Микола asked for contents near the top bar
          consistently across auth *and* checkout, and this page was still centring them. */}
      <main className="flex-1 flex items-start justify-center px-6 pt-[32px] md:pt-[64px]">
        <div className="w-[520px] flex flex-col items-center gap-8 text-center">
          <CheckBadge />

          <h1 className="text-[40px] leading-[48px] font-extrabold text-brand-dark">
            You're all set!
          </h1>

          <p className="text-[18px] leading-[28px] text-brand-dark/85">
            {isTrial
              ? 'Your subscription is active. Your free trial has started — you won’t be charged until it ends.'
              : 'Your subscription is active. A receipt has been sent to your email.'}
            {planLabel && (
              <>
                <br />
                <span className="text-brand-green font-semibold">
                  Plan: {planLabel} ({billingLabel})
                </span>
              </>
            )}
          </p>

          <button
            type="button"
            onClick={() => navigate('/pipeline')}
            className="w-[264px] h-[64px] rounded-full bg-brand-green text-white text-[16px] font-semibold"
          >
            Go to Dashboard
          </button>
        </div>
      </main>
    </div>
  )
}

export default PaymentSuccess
